import {
    collection,
    addDoc,
    updateDoc,
    getDoc,
    getDocs,
    doc,
    query,
    where,
    orderBy,
    serverTimestamp,
runTransaction,
} from "firebase/firestore";

import { db } from "../../firebase";
import {
    HOLDER_TYPES
} from "../../constants/holderTypes";
import {
    ASSET_STATUS
} from "../../constants/enums/assetStatus";


import {
    normalizeHolderType,
    buildHolderSnapshot
} from "../../utils/assets/holderUtils";

import {
    MOVEMENT_TYPES
} from "../../constants/enums/movementTypes";

import {
    createHolderSnapshot
} from "../../utils/holderSnapshotFactory";

// ======================================================
// COLLECTION
// ======================================================

const ASSET_MASTER_COLLECTION = "asset_master";

// ======================================================
// CONSTANTS
// ======================================================

export const OWNERSHIP_MODE = {
    PERSONAL: "PERSONAL",
    RETURNABLE: "RETURNABLE",
};



// ======================================================
// VALIDATION HELPERS
// ======================================================

const validateCompany = (companyId) => {
    if (!companyId) {
        throw new Error("company_id is required");
    }
};

const validateOwnershipMode = (
    ownershipMode
) => {
    const allowedModes = Object.values(
        OWNERSHIP_MODE
    );

    if (
        !allowedModes.includes(
            ownershipMode
        )
    ) {
        throw new Error(
            "Invalid ownership_mode"
        );
    }
};

const validateHolderType = (
    holderType
) => {
    const allowedTypes = Object.values(
        HOLDER_TYPES
    );

    if (
        !allowedTypes.includes(
            holderType
        )
    ) {
        throw new Error(
            "Invalid holder type"
        );
    }
};

// ======================================================
// CREATE ASSET
// ======================================================

export const createAsset = async ({
    asset_name,
    asset_category,
    asset_specification = "",
    asset_type = "",
    ownership_mode,
    serial_no = "",
    current_holder_type =
    HOLDER_TYPES.STORE,
    current_holder_id = null,
    status = ASSET_STATUS.ACTIVE,
    remarks = "",
    created_by,
    company_id,
}) => {
    validateCompany(company_id);

    if (!asset_name) {
        throw new Error(
            "asset_name is required"
        );
    }

    if (!asset_category) {
        throw new Error(
            "asset_category is required"
        );
    }

    if (!ownership_mode) {
        throw new Error(
            "ownership_mode is required"
        );
    }

    if (!created_by) {
        throw new Error(
            "created_by is required"
        );
    }

    validateOwnershipMode(
        ownership_mode
    );

    const normalizedHolderType =
    normalizeHolderType(
        current_holder_type
    );

validateHolderType(
    normalizedHolderType
);

const holderSnapshot =
    createHolderSnapshot({
        holderType:
            normalizedHolderType,

        holderId:
            current_holder_id,

        collection: []
    });

    // ==============================================
    // SERIAL DUPLICATE CHECK
    // ==============================================

    if (serial_no) {
        const serialQuery = query(
            collection(
                db,
                ASSET_MASTER_COLLECTION
            ),
            where(
                "serial_no",
                "==",
                serial_no
            ),
            where(
                "company_id",
                "==",
                company_id
            )
        );

        const serialSnapshot =
            await getDocs(serialQuery);

        if (!serialSnapshot.empty) {
            throw new Error(
                "Asset serial number already exists"
            );
        }
    }

    // ==============================================
    // ASSET PAYLOAD
    // ==============================================

    const assetPayload = {
        asset_name:
            asset_name.trim(),

        asset_category:
    asset_category.trim(),

asset_specification:
    asset_specification?.trim() || "",

            

        asset_type:
            asset_type || "",

        ownership_mode,

        serial_no:
    serial_no
        ? serial_no.trim().toUpperCase()
        : "",

        current_holder_type:
    normalizedHolderType,

current_holder_id,

holder_snapshot:
    holderSnapshot,

        status,

        remarks,

        company_id,

        created_by,

        created_at:
            serverTimestamp(),

        updated_at:
            serverTimestamp(),
    };

    const assetRef = await addDoc(
        collection(
            db,
            ASSET_MASTER_COLLECTION
        ),
        assetPayload
    );

    // ==============================================
    // OPTIONAL ASSET CODE
    // ==============================================

    const asset_id = assetRef.id;

    await updateDoc(assetRef, {
        asset_id,
    });

    // ==============================================
    // INITIAL_STOCK TRANSACTION — SSOT entry
    // Har naye asset ka pehla ledger entry
    // ==============================================
    await addDoc(collection(db, "asset_transactions"), {
        asset_id,
        asset_name:        asset_name.trim(),
        serial_no:         serial_no ? serial_no.trim().toUpperCase() : "",
        movement_type:     MOVEMENT_TYPES.INITIAL_STOCK,
        from_type:         null,
        from_id:           null,
        from_holder_name:  null,
        to_type:           normalizedHolderType,
        to_id:             current_holder_id || null,
        to_holder_name:    null,
        status:            "FINALIZED",
        workflow_state:    "FINALIZED",
        company_id,
        requested_by:      created_by,
        requested_by_name: "",
        approved_by:       created_by,
        remarks:           remarks || "Initial stock entry",
        created_at:        serverTimestamp(),
        updated_at:        serverTimestamp(),
    });

    return {
        success: true,
        asset_id,
    };
};

    // ======================================================
    // UPDATE ASSET
    // ======================================================

    export const updateAsset = async ({
        asset_id,
        asset_name,
        asset_category,
        asset_type,
    ownership_mode,
    asset_specification,
    serial_no,
    status,
    remarks,
    updated_by,
    company_id,
}) => {
    validateCompany(company_id);

    if (!asset_id) {
        throw new Error(
            "asset_id is required"
        );
    }

    if (!updated_by) {
        throw new Error(
            "updated_by is required"
        );
    }

    const assetRef = doc(
        db,
        ASSET_MASTER_COLLECTION,
        asset_id
    );

    const assetSnap = await getDoc(
        assetRef
    );

    if (!assetSnap.exists()) {
        throw new Error(
            "Asset not found"
        );
    }

    const existingAsset =
        assetSnap.data();

    if (
        existingAsset.company_id !==
        company_id
    ) {
        throw new Error(
            "Unauthorized company access"
        );
    }

    // ==============================================
    // SERIAL DUPLICATE CHECK
    // ==============================================

    if (
        serial_no &&
        serial_no !==
            existingAsset.serial_no
    ) {
        const serialQuery = query(
            collection(
                db,
                ASSET_MASTER_COLLECTION
            ),
            where(
                "serial_no",
                "==",
                serial_no
            ),
            where(
                "company_id",
                "==",
                company_id
            )
        );

        const serialSnapshot =
            await getDocs(serialQuery);

        const duplicateAsset =
            serialSnapshot.docs.find(
                (docItem) =>
                    docItem.id !== asset_id
            );

        if (duplicateAsset) {
            throw new Error(
                "Duplicate serial number detected"
            );
        }
    }

    // ==============================================
    // IMPORTANT:
    // NEVER UPDATE HOLDER HERE
    // HOLDER MUST COME FROM REPLAY
    // ==============================================

    const updatePayload = {
        asset_name:
            asset_name ??
            existingAsset.asset_name,

        asset_category:
            asset_category ??
            existingAsset.asset_category,

        asset_type:
            asset_type ??
            existingAsset.asset_type,

        ownership_mode:
            ownership_mode ??
            existingAsset.ownership_mode,

        asset_specification:
            asset_specification ??
            existingAsset.asset_specification,

        serial_no:
    serial_no
        ? serial_no.trim().toUpperCase()
        : existingAsset.serial_no,

        status:
            status ??
            existingAsset.status,

        remarks:
            remarks ??
            existingAsset.remarks,

        updated_by,

        updated_at:
            serverTimestamp(),
    };

    await updateDoc(
        assetRef,
        updatePayload
    );

    return {
        success: true,
        asset_id,
    };
};

// ======================================================
// GET ASSET BY ID
// ======================================================

export const getAssetById = async (
    asset_id,
    company_id
) => {
    validateCompany(company_id);

    if (!asset_id) {
        throw new Error(
            "asset_id is required"
        );
    }

    const assetRef = doc(
        db,
        ASSET_MASTER_COLLECTION,
        asset_id
    );

    const assetSnap = await getDoc(
        assetRef
    );

    if (!assetSnap.exists()) {
        throw new Error(
            "Asset not found"
        );
    }

    const asset =
    assetSnap.data();

if (
    asset.company_id !==
    company_id
) {
    throw new Error(
        "Unauthorized company access"
    );
}

// ==============================================
// LEGACY SNAPSHOT FALLBACK
// ==============================================

if (
    !asset?.holder_snapshot
) {

    asset.holder_snapshot =
        buildHolderSnapshot({

            holderType:
                asset?.current_holder_type,

            holderId:
                asset?.current_holder_id,

            collection: []

        });

}

return {
    id: assetSnap.id,
    ...asset,
};
};

// ======================================================
// GET ALL ASSETS
// ======================================================

export const getAssets = async ({
    company_id,
    status = null,
    ownership_mode = null,
    holder_type = null,
}) => {
    validateCompany(company_id);

    let q = query(
        collection(
            db,
            ASSET_MASTER_COLLECTION
        ),
        where(
            "company_id",
            "==",
            company_id
        ),
        orderBy("created_at", "desc")
    );

    const snapshot = await getDocs(
        q
    );

    let assets = snapshot.docs.map(
        (docItem) => ({
            id: docItem.id,
            ...docItem.data(),
        })
    );

    // ==============================================
    // LOCAL FILTERS
    // ==============================================

    if (status) {
        assets = assets.filter(
            (asset) =>
                asset.status === status
        );
    }

    if (ownership_mode) {
        assets = assets.filter(
            (asset) =>
                asset.ownership_mode ===
                ownership_mode
        );
    }

    if (holder_type) {

    const normalizedHolderType =
        normalizeHolderType(
            holder_type
        );

    assets = assets.filter(
        (asset) =>

            normalizeHolderType(
                asset.current_holder_type
            ) ===
            normalizedHolderType

    );

}

    return assets;
};

// ======================================================
// ARCHIVE ASSET
// ======================================================

export const archiveAsset = async ({
    asset_id,
    archived_by,
    company_id,
}) => {
    validateCompany(company_id);

    if (!asset_id) {
        throw new Error(
            "asset_id is required"
        );
    }

    if (!archived_by) {
        throw new Error(
            "archived_by is required"
        );
    }

    const assetRef = doc(
        db,
        ASSET_MASTER_COLLECTION,
        asset_id
    );

    const assetSnap = await getDoc(
        assetRef
    );

    if (!assetSnap.exists()) {
        throw new Error(
            "Asset not found"
        );
    }

    const asset =
        assetSnap.data();

    if (
        asset.company_id !==
        company_id
    ) {
        throw new Error(
            "Unauthorized company access"
        );
    }

    await updateDoc(assetRef, {
        status:
            ASSET_STATUS.INACTIVE,

        archived_by,

        archived_at:
            serverTimestamp(),

        updated_at:
            serverTimestamp(),
    });

    return {
        success: true,
        asset_id,
    };
};

// ======================================================
// SCRAP ASSET
// ======================================================

export const scrapAsset = async ({
    asset_id,
    scrapped_by,
    scrap_reason = "",
    company_id,
}) => {
    validateCompany(company_id);

    if (!asset_id) {
        throw new Error(
            "asset_id is required"
        );
    }

    if (!scrapped_by) {
        throw new Error(
            "scrapped_by is required"
        );
    }

    const assetRef = doc(
        db,
        ASSET_MASTER_COLLECTION,
        asset_id
    );

    const assetSnap = await getDoc(
        assetRef
    );

    if (!assetSnap.exists()) {
        throw new Error(
            "Asset not found"
        );
    }

    const asset =
        assetSnap.data();

    if (
        asset.company_id !==
        company_id
    ) {
        throw new Error(
            "Unauthorized company access"
        );
    }

    await runTransaction(
    db,
    async (transaction) => {

        // ==========================================
        // CREATE IMMUTABLE TRANSACTION EVENT
        // ==========================================

        const transactionRef = doc(
            collection(
                db,
                "asset_transactions"
            )
        );

        transaction.set(
            transactionRef,
            {

                asset_id,

                asset_name:
                    asset.asset_name || "",

                serial_no:
                    asset.serial_no || "",

                movement_type:
    MOVEMENT_TYPES.SCRAP,

                from_type:
                    asset.current_holder_type || null,

                from_id:
                    asset.current_holder_id || null,

                to_type:
    "SCRAP",

                to_id:
                    null,

                status:
                    "COMPLETED",

                company_id,

                remarks:
                    scrap_reason || "",

                requested_by:
                    scrapped_by,

                approved_by:
                    scrapped_by,

                created_at:
                    serverTimestamp(),

                updated_at:
                    serverTimestamp(),
            }
        );

        // ==========================================
        // UPDATE SNAPSHOT CACHE ONLY
        // ==========================================

        transaction.update(
            assetRef,
            {

                status:
                    ASSET_STATUS.SCRAPPED,

                current_holder_type:
    "SCRAP",

                current_holder_id:
                    null,

                holder_snapshot:
                    createHolderSnapshot({

                        holderType:
                            "SCRAP",

                        holderId:
                            null,

                        collection: [],

                        snapshotSource:
                            "REPLAY_CACHE"
                    }),

                scrap_reason,

                scrapped_by,

                scrapped_at:
                    serverTimestamp(),

                updated_at:
                    serverTimestamp(),
            }
        );
    }
);

    return {
        success: true,
        asset_id,
    };
};

// ======================================================
// MARK LOST ASSET
// ======================================================

export const markAssetLost = async ({
    asset_id,
    lost_by,
    loss_reason = "",
    company_id,
}) => {
    validateCompany(company_id);

    if (!asset_id) {
        throw new Error(
            "asset_id is required"
        );
    }

    if (!lost_by) {
        throw new Error(
            "lost_by is required"
        );
    }

    const assetRef = doc(
        db,
        ASSET_MASTER_COLLECTION,
        asset_id
    );

    const assetSnap = await getDoc(
        assetRef
    );

    if (!assetSnap.exists()) {
        throw new Error(
            "Asset not found"
        );
    }

    const asset =
        assetSnap.data();

    if (
        asset.company_id !==
        company_id
    ) {
        throw new Error(
            "Unauthorized company access"
        );
    }

    await runTransaction(
    db,
    async (transaction) => {

        // ==========================================
        // IMMUTABLE LOST EVENT
        // ==========================================

        const transactionRef = doc(
            collection(
                db,
                "asset_transactions"
            )
        );

        transaction.set(
            transactionRef,
            {

                asset_id,

                asset_name:
                    asset.asset_name || "",

                serial_no:
                    asset.serial_no || "",

                movement_type:
                    "LOST",

                from_type:
                    asset.current_holder_type || null,

                from_id:
                    asset.current_holder_id || null,

                to_type:
                    "LOST",

                to_id:
                    null,

                status:
                    "COMPLETED",

                company_id,

                remarks:
                    loss_reason || "",

                requested_by:
                    lost_by,

                approved_by:
                    lost_by,

                created_at:
                    serverTimestamp(),

                updated_at:
                    serverTimestamp(),
            }
        );

        // ==========================================
        // UPDATE CACHE SNAPSHOT
        // ==========================================

        transaction.update(
            assetRef,
            {

                status:
                    ASSET_STATUS.LOST,

                current_holder_type:
                    "LOST",

                current_holder_id:
                    null,

                holder_snapshot:
                    createHolderSnapshot({

                        holderType:
                            "LOST",

                        holderId:
                            null,

                        collection: [],

                        snapshotSource:
                            "REPLAY_CACHE"
                    }),

                loss_reason,

                lost_by,

                lost_at:
                    serverTimestamp(),

                updated_at:
                    serverTimestamp(),
            }
        );
    }
);

    return {
        success: true,
        asset_id,
    };
};